import {
  ContentLayout,
  ISurveyContentBlock,
  ISurveyOption,
  ISurveyQuestion,
  QuestionType,
  SkipOptions,
  SurveyContentBlock,
  SurveyOption,
} from 'src/services/api/api-client';
import { createCommonOption } from './option-helpers';

type QuestionPreset = {
  optionCount: number;
  optionFactory?: (index: number) => SurveyOption;
  canAddOptions: boolean;
  canDeleteOptions: boolean;
  usesContentBlocks?: boolean;
  minContentBlocks?: number;
};

const buildOption = (optionText: string, optionValue: string, scoreValue: number): SurveyOption =>
  createCommonOption({
    optionText,
    optionValue,
    scoreValue,
  });

const createYesNoOption = (index: number): SurveyOption =>
  buildOption(index === 0 ? 'Yes' : 'No', index === 0 ? 'true' : 'false', index === 0 ? 1 : 0);

const createDateInputOption = (): SurveyOption => buildOption('Date', '', 0);

const createDateRangeOption = (index: number): SurveyOption =>
  buildOption(index === 0 ? 'Start date' : 'End date', '', 0);

const createDefaultOption = (index: number): SurveyOption => buildOption(`Option${index}`, `Value${index}`, index);
const createSliderOption = (index: number): SurveyOption => buildOption(`Option ${index}`, index.toString(), index);
const createContentBlock = (): SurveyContentBlock =>
  SurveyContentBlock.fromJS({
    titleText: '',
    descriptionText: '',
    image: null,
    layout: ContentLayout.Default,
  });

const QUESTION_PRESETS: Record<QuestionType, QuestionPreset> = {
  [QuestionType.SingleChose]: {
    optionCount: 1,
    optionFactory: createDefaultOption,
    canAddOptions: true,
    canDeleteOptions: true,
  },
  [QuestionType.MultipleChose]: {
    optionCount: 1,
    optionFactory: createDefaultOption,
    canAddOptions: true,
    canDeleteOptions: true,
  },
  [QuestionType.Rate]: {
    optionCount: 1,
    optionFactory: createSliderOption,
    canAddOptions: true,
    canDeleteOptions: true,
  },
  [QuestionType.Scale]: {
    optionCount: 1,
    optionFactory: createSliderOption,
    canAddOptions: true,
    canDeleteOptions: true,
  },
  [QuestionType.FreeInput]: {
    optionCount: 1,
    optionFactory: () => buildOption('Comment', '0', 0),
    canAddOptions: false,
    canDeleteOptions: false,
  },
  [QuestionType.ConditionalQuestion]: {
    optionCount: 1,
    optionFactory: createDefaultOption,
    canAddOptions: true,
    canDeleteOptions: true,
  },
  [QuestionType.YesNo]: {
    optionCount: 2,
    optionFactory: createYesNoOption,
    canAddOptions: false,
    canDeleteOptions: false,
  },
  [QuestionType.DateInput]: {
    optionCount: 1,
    optionFactory: () => createDateInputOption(),
    canAddOptions: false,
    canDeleteOptions: false,
  },
  [QuestionType.DateRangeInput]: {
    optionCount: 2,
    optionFactory: createDateRangeOption,
    canAddOptions: false,
    canDeleteOptions: false,
  },
  [QuestionType.Content]: {
    optionCount: 0,
    canAddOptions: false,
    canDeleteOptions: false,
    usesContentBlocks: true,
    minContentBlocks: 1,
  },
  [QuestionType.Gallery]: {
    optionCount: 0,
    canAddOptions: false,
    canDeleteOptions: false,
    usesContentBlocks: true,
    minContentBlocks: 1,
  },
};

export const getQuestionTypeLabel = (questionType: QuestionType): string => {
  switch (questionType) {
    case QuestionType.SingleChose:
      return 'Single choice';
    case QuestionType.MultipleChose:
      return 'Multiple choice';
    case QuestionType.Rate:
      return 'Rate';
    case QuestionType.Scale:
      return 'Scale';
    case QuestionType.FreeInput:
      return 'Free input';
    case QuestionType.YesNo:
      return 'Yes / No';
    case QuestionType.DateInput:
      return 'Date input';
    case QuestionType.DateRangeInput:
      return 'Date range';
    case QuestionType.Content:
      return 'Content';
    case QuestionType.Gallery:
      return 'Gallery';
    default:
      return QuestionType[questionType];
  }
};

export const getQuestionPreset = (questionType: QuestionType): QuestionPreset => QUESTION_PRESETS[questionType];

export const normalizeQuestionContentBlocks = (
  questionType: QuestionType,
  contentBlocks: ISurveyContentBlock[] | null | undefined,
): SurveyContentBlock[] | null => {
  const preset = getQuestionPreset(questionType);

  if (!preset.usesContentBlocks) {
    return null;
  }

  const currentBlocks = (contentBlocks ?? []).map((block) => SurveyContentBlock.fromJS(block));
  const minimumCount = preset.minContentBlocks ?? 0;
  const nextCount = Math.max(minimumCount, currentBlocks.length);

  if (!currentBlocks.length) {
    return Array.from({ length: nextCount }, () => createContentBlock());
  }

  return Array.from({ length: nextCount }, (_, index) => currentBlocks[index] ?? createContentBlock());
};

export const normalizeQuestionOptions = (
  questionType: QuestionType,
  options: ISurveyOption[] | undefined,
): SurveyOption[] => {
  const preset = getQuestionPreset(questionType);
  if (preset.usesContentBlocks) {
    return [];
  }
  const currentOptions = (options ?? []).map((option) => SurveyOption.fromJS(option));
  const nextCount = Math.max(preset.optionCount, currentOptions.length);

  if (!preset.canAddOptions && currentOptions.length !== preset.optionCount) {
    return Array.from({ length: preset.optionCount }, (_, index) => {
      const currentOption = currentOptions[index];
      const fallbackOption = preset.optionFactory?.(index) ?? createDefaultOption(index);
      return currentOption
        ? SurveyOption.fromJS({
            ...fallbackOption.toJSON(),
            ...currentOption.toJSON(),
          })
        : fallbackOption;
    });
  }

  if (!currentOptions.length) {
    return Array.from(
      { length: preset.optionCount },
      (_, index) => preset.optionFactory?.(index) ?? createDefaultOption(index),
    );
  }

  return Array.from({ length: nextCount }, (_, index) => {
    const currentOption = currentOptions[index];
    return currentOption ?? preset.optionFactory?.(index) ?? createDefaultOption(index);
  });
};

export const normalizeQuestionByType = (question: ISurveyQuestion, nextType: QuestionType): ISurveyQuestion => {
  const options = normalizeQuestionOptions(nextType, question.options);
  const contentBlock = normalizeQuestionContentBlocks(nextType, question.contentBlock);

  return {
    ...question,
    discriminator:
      nextType === QuestionType.SingleChose
        ? 'SingleChoseQuestion'
        : nextType === QuestionType.MultipleChose
        ? 'MultipleChoseQuestion'
        : nextType === QuestionType.Rate
        ? 'RateQuestion'
        : nextType === QuestionType.Scale
        ? 'ScaleQuestion'
        : nextType === QuestionType.FreeInput
        ? 'FreeInputQuestion'
        : nextType === QuestionType.YesNo
        ? 'YesNoQuestion'
        : nextType === QuestionType.DateInput
        ? 'DateInputQuestion'
        : nextType === QuestionType.DateRangeInput
        ? 'DateRangeInputQuestion'
        : `${QuestionType[nextType]}Question`,
    isYesNoQuestion: nextType === QuestionType.YesNo,
    questionType: nextType,
    isSkippedRender: question.isSkippedRender ?? SkipOptions.NotSkip,
    options,
    contentBlock,
  };
};

export const isFixedOptionCountQuestion = (questionType: QuestionType): boolean =>
  !getQuestionPreset(questionType).canAddOptions;
